import { offlineContext } from "@/lib/offline/context";
import { removeLibraryItem, readLibraryItem, type LibraryContext } from "@/native-shell/library";
import { saveDownload } from "@/lib/offline/downloads";
import { withOutingDownloadLock } from "./outing-download-lock";
import { openCompanionDb } from "./device-database";
import { canChangeOutingState, validateOutingMutation, type Outing, type OutingContent, type OutingMutation, type OutingState } from "./outings";
import { canAdvanceRoute, outingRouteContext, parseOutingRoute, routeMatchesOuting, routeStateFromSaved, routeWithProgress, validateRouteMutation, type OutingRouteMutation, type OutingRouteSnapshot, type OutingRouteState } from "./outing-routes";
import type { SavedRoute } from "@/lib/planner/saved-route";

export const OUTINGS_CHANGED = "peakwave-outings-changed";
export type DeviceOuting = {
  key: string; owner: string; current: Outing; deviceRevision: number;
  pending: (OutingMutation | OutingRouteMutation)[]; conflict: Outing | null; error: string | null;
  needsReview: boolean; deleted: boolean;
  routes?: OutingRouteSnapshot[];
  downloadCleanupPending?: boolean;
};
const STORE = "outings";
const DELETIONS = "outing-deletions";
type DeletionReceipt = { key: string; owner: string; id: string; ticket: string; status: "pending" | "complete"; removeRow: boolean };
const MAX_BYTES = 8 * 1024 * 1024;
const changed = () => window.dispatchEvent(new Event(OUTINGS_CHANGED));
async function assertContext(expected: LibraryContext) {
  const current = await offlineContext();
  if (current.owner !== expected.owner || current.generation !== expected.generation) throw new Error("Your account changed. Reopen your plans before continuing.");
}
function transaction<T>(db: IDBDatabase, stores: string[], run: (tx: IDBTransaction, done: (value: T) => void) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(stores, "readwrite");
    let result: T;
    tx.oncomplete = () => resolve(result);
    tx.onabort = () => reject(tx.error ?? new Error("The plan changed in another tab. Reload it before editing."));
    tx.onerror = () => reject(tx.error ?? new Error("Your device could not save this plan. Free storage and retry."));
    try { run(tx, value => { result = value; }); } catch (error) { tx.abort(); reject(error); }
  });
}
async function readForOwner(owner: string): Promise<DeviceOuting[]> {
  const db = await openCompanionDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const request = tx.objectStore(STORE).index("owner").getAll(owner);
      request.onsuccess = () => resolve(request.result as DeviceOuting[]);
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}
export async function listDeviceOutings(context: LibraryContext): Promise<DeviceOuting[]> {
  await assertContext(context);
  const rows = await readForOwner(context.owner);
  await assertContext(context);
  return rows.sort((a, b) => b.current.updated_at.localeCompare(a.current.updated_at));
}
async function mutateRecord(context: LibraryContext, id: string, update: (previous: DeviceOuting | undefined, removed: boolean) => DeviceOuting | null, missing = false): Promise<DeviceOuting | null> {
  await assertContext(context);
  const db = await openCompanionDb();
  let failure: unknown;
  try {
    const result = await transaction<DeviceOuting | null>(db, [STORE, DELETIONS], (tx, done) => {
      const store = tx.objectStore(STORE);
      const deletions = tx.objectStore(DELETIONS), key = `${context.owner}:${id}`;
      const receiptRequest = deletions.get(key);
      receiptRequest.onsuccess = () => {
        const receipt = receiptRequest.result as DeletionReceipt | undefined;
        const request = store.index("owner").getAll(context.owner);
        request.onsuccess = () => {
          try {
            const rows = request.result as DeviceOuting[];
            const previous = rows.find(row => row.key === key);
            let next = update(previous, !!receipt);
            if (next && !previous && receipt) throw new Error("This outing was removed. Start a new outing to keep a separate copy.");
            const needsCleanup = (next?.deleted || missing && !next) && !receipt;
            if (next?.deleted) next = { ...next, downloadCleanupPending: needsCleanup || receipt?.status === "pending" };
            const remaining = rows.filter(row => row.key !== key);
            if (next) remaining.push(next);
            if (remaining.length > 200 || remaining.reduce((sum, row) => sum + row.pending.length, 0) > 100
              || new TextEncoder().encode(JSON.stringify(remaining)).byteLength > MAX_BYTES) {
              failure = new Error("Device plan storage is full. Sync or remove an old plan, then retry. Your previous copy is kept.");
              tx.abort(); return;
            }
            if (needsCleanup) {
              deletions.put({ key, owner: context.owner, id, ticket: crypto.randomUUID(), status: "pending", removeRow: missing || context.owner === "device" } satisfies DeletionReceipt);
            } else if (previous?.deleted && next && !next.deleted) {
              // Only the explicit conflict review can restore a deleted row.
              deletions.delete(key);
            }
            if (next) store.put(next); else store.delete(key);
            done(next);
          } catch (error) { failure = error; tx.abort(); }
        };
      };
    }).catch(error => { throw failure ?? error; });
    await assertContext(context);
    changed(); return result;
  } finally { db.close(); }
}
export async function saveDeviceOuting(context: LibraryContext, input: {
  id: string; deviceRevision: number; state: OutingState; content: OutingContent; baseId: string | null; operation?: "save" | "delete";
  route?: SavedRoute;
}): Promise<DeviceOuting | null> {
  const result = await mutateRecord(context, input.id, previous => {
    if ((previous?.deviceRevision ?? 0) !== input.deviceRevision || previous?.needsReview || previous?.deleted) throw new Error("Review the changed plan first.");
    if (previous && !canChangeOutingState(previous.current.state, input.state)) throw new Error("This outing cannot make that change.");
    const expectedRevision = previous?.current.revision ?? 0;
    const mutation: OutingMutation = { mutationId: crypto.randomUUID(), owner: context.owner, id: input.id,
      expectedRevision, operation: input.operation ?? "save", baseId: input.baseId, state: input.state, content: input.content };
    const problem = validateOutingMutation(mutation); if (problem) throw new Error(problem);
    if (previous && ["in_progress", "completed", "cancelled"].includes(previous.current.state)) {
      const before = { ...previous.current.content, privateNote: null };
      const after = { ...input.content, privateNote: null };
      if (JSON.stringify(before) !== JSON.stringify(after) || previous.current.base_id !== input.baseId) throw new Error("Started outings keep their original plan.");
    }
    const routeChanged = previous && outingRouteContext(previous.current.content) !== outingRouteContext(input.content);
    if (routeChanged && previous.current.route_state && previous.current.route_state.status !== "saved") throw new Error("A started route keeps its original outing. Save a new outing for a different day.");
    if (!previous && !["idea", "saved"].includes(input.state)) throw new Error("Save an idea or outing first.");
    if (mutation.operation === "delete" && !previous) throw new Error("Open the saved outing before removing it.");
    const now = new Date().toISOString();
    const result: DeviceOuting = { key: `${context.owner}:${input.id}`, owner: context.owner, deviceRevision: input.deviceRevision + 1,
      current: { id: input.id, user_id: context.owner, base_id: input.baseId, revision: expectedRevision + 1, state: input.state, content: input.content, route_state: routeChanged ? null : previous?.current.route_state ?? null, created_at: previous?.current.created_at ?? now, updated_at: now },
      pending: context.owner === "device" ? [] : [...previous?.pending ?? [], mutation],
      routes: previous?.routes ?? [],
      conflict: null, error: null, needsReview: false, deleted: mutation.operation === "delete" };
    return input.route ? withRoute(result, input.route) : result;
  });
  if (input.operation !== "delete") return result;
  await finishOutingDownloadDeletion(context, input.id);
  return context.owner === "device" ? null : (await listDeviceOutings(context)).find(row => row.current.id === input.id) ?? null;
}
export async function cacheServerOutings(context: LibraryContext, outings: Outing[]): Promise<void> {
  for (const outing of outings) {
    if (outing.user_id !== context.owner) throw new Error("The returned plans belong to a different account.");
    await mutateRecord(context, outing.id, (previous, removed) => {
      if (removed && !previous) return null;
      if (previous?.deleted || previous?.pending.length || (previous && previous.current.revision >= outing.revision)) return previous;
      return { key: `${context.owner}:${outing.id}`, owner: context.owner, current: outing, deviceRevision: (previous?.deviceRevision ?? 0) + 1,
        routes: previous?.routes ?? [],
        pending: [], conflict: null, error: null, needsReview: false, deleted: false };
    });
  }
}
export async function removeMissingOutings(context: LibraryContext, ids: string[]): Promise<void> {
  for (const id of ids) {
    const retained = await mutateRecord(context, id, previous => previous?.pending.length ? previous : previous ? { ...previous, deleted: true, deviceRevision: previous.deviceRevision + 1 } : null, true);
    if (!retained?.pending.length) await finishOutingDownloadDeletion(context, id);
  }
}
/** Download the reviewed device revision, never fields retained by an old tab. */
export async function saveOutingDownload(context: LibraryContext, id: string, deviceRevision: number): Promise<void> {
  await withOutingDownloadLock(context, id, async () => {
    const row = (await listDeviceOutings(context)).find(row => row.current.id === id);
    if (!row || row.deleted || row.needsReview || row.deviceRevision !== deviceRevision) throw new Error("The outing changed. Reopen it before downloading a copy.");
    const outing = row.current;
    await saveDownload(`outing:${id}`, "outing", outing.content.title, { id, revision: outing.revision, state: outing.state, content: outing.content }, new Date().toISOString(), context);
    await assertContext(context);
  }, true);
}
async function readDeletion(context: LibraryContext, id: string): Promise<DeletionReceipt | undefined> {
  await assertContext(context);
  const db = await openCompanionDb();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(DELETIONS).objectStore(DELETIONS).get(`${context.owner}:${id}`);
      request.onsuccess = () => resolve(request.result as DeletionReceipt | undefined);
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}
/** A failed library removal retains the intent and original row for retry. */
async function finishOutingDownloadDeletion(context: LibraryContext, id: string): Promise<void> {
  await withOutingDownloadLock(context, id, async () => {
    const receipt = await readDeletion(context, id);
    if (!receipt || receipt.status === "complete") return;
    await assertContext(context);
    await removeLibraryItem(`outing:${id}`, context);
    if (await readLibraryItem(`outing:${id}`, context) !== null) throw new Error("The downloaded copy is still present. Retry removal before finishing deletion.");
    await assertContext(context);
    const db = await openCompanionDb();
    let failure: unknown;
    try {
      await transaction<void>(db, [STORE, DELETIONS], (tx, done) => {
        const deletions = tx.objectStore(DELETIONS), request = deletions.get(receipt.key);
        request.onsuccess = () => {
          const current = request.result as DeletionReceipt | undefined;
          if (current?.ticket !== receipt.ticket || current.status !== "pending") return done();
          const store = tx.objectStore(STORE), outing = store.get(receipt.key);
          outing.onsuccess = () => {
            try {
              const row = outing.result as DeviceOuting | undefined;
              if (row && !row.deleted) return done();
              if (row) {
                if (receipt.removeRow) store.delete(receipt.key);
                else store.put({ ...row, downloadCleanupPending: false });
              }
              deletions.put({ ...receipt, status: "complete" });
              done();
            } catch (error) { failure = error; tx.abort(); }
          };
        };
      }).catch(error => { throw failure ?? error; });
    } finally { db.close(); }
    await assertContext(context);
    window.dispatchEvent(new Event("peakwave-offline-downloads"));
    changed();
  });
}
async function retryOutingDownloadDeletions(context: LibraryContext): Promise<void> {
  // Upgrade earlier deleted rows lazily, without touching live tracker stores.
  for (const row of await listDeviceOutings(context)) if (row.deleted && row.downloadCleanupPending === undefined) {
    await mutateRecord(context, row.current.id, current => current ?? null);
  }
  const db = await openCompanionDb();
  let pending: DeletionReceipt[];
  try {
    pending = await new Promise((resolve, reject) => {
      const request = db.transaction(DELETIONS).objectStore(DELETIONS).index("pending").getAll([context.owner, "pending"], 200);
      request.onsuccess = () => resolve(request.result as DeletionReceipt[]);
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
  for (const receipt of pending) await finishOutingDownloadDeletion(context, receipt.id);
}
/** Ordered mutations, immutable request IDs and account checks before and after each await. */
export async function syncDeviceOutings(context: LibraryContext): Promise<void> {
  const work = async () => {
    await retryOutingDownloadDeletions(context);
    if (context.owner === "device" || !navigator.onLine) return;
    const rows = await listDeviceOutings(context);
    for (const row of rows) {
      if (row.needsReview || row.downloadCleanupPending) continue;
      for (const mutation of row.pending) {
        await assertContext(context);
        let response: Response;
        try { response = await fetch(mutation.operation === "route" ? `/api/companion/outings/${mutation.id}/route` : "/api/companion/outings", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(mutation) }); }
        catch { await markFailure(context, row.current.id, "Saved on this device. Waiting for a connection."); return; }
        const result = await response.json() as { outing?: Outing; error?: string; deleted?: boolean; current?: Outing | null };
        await assertContext(context);
        if (!response.ok) {
          const review = [400, 404, 409, 410].includes(response.status);
          await mutateRecord(context, row.current.id, current => current ? { ...current, needsReview: review, conflict: result.current ?? null, error: result.error ?? "Sync failed. Retry when connected." } : null);
          if ([401, 403].includes(response.status)) return;
          break;
        }
        if (!result.deleted && (!result.outing || result.outing.user_id !== context.owner || result.outing.id !== mutation.id || result.outing.revision !== mutation.expectedRevision + 1)) throw new Error("The saved plan could not be verified. Your draft is retained.");
        await mutateRecord(context, row.current.id, current => {
          if (!current || current.pending[0]?.mutationId !== mutation.mutationId) return current ?? null;
          const pending = current.pending.slice(1);
          if (result.deleted && pending.length === 0) return null;
          return { ...current, pending, current: pending.length ? current.current : result.outing!, error: null };
        });
      }
    }
  };
  if (navigator.locks) await navigator.locks.request("peakwave-outing-sync", { ifAvailable: true }, lock => lock ? work() : undefined);
  else await work(); // Duplicate transports use the same server receipt; no competing GPS journal.
}
async function markFailure(context: LibraryContext, id: string, error: string) {
  await mutateRecord(context, id, current => current ? { ...current, error } : null);
}
export async function resolveOutingConflict(context: LibraryContext, id: string, choice: "saved" | "copy"): Promise<string | null> {
  const row = (await listDeviceOutings(context)).find(item => item.current.id === id);
  if (!row?.needsReview) return null;
  let copyId: string | null = null;
  if (choice === "copy") {
    copyId = crypto.randomUUID();
    const route = row.routes?.find(route => route.id === row.current.route_state?.snapshotId)?.route;
    await saveDeviceOuting(context, { id: copyId, deviceRevision: 0, content: { ...row.current.content, title: `${row.current.content.title.slice(0, 90)} (copy)` },
      baseId: null, state: row.current.content.startAt ? "saved" : "idea",
      route: route && row.current.route_state ? routeWithProgress(route, row.current.route_state) : undefined });
  }
  await mutateRecord(context, id, current => {
    if (!current || current.deviceRevision !== row.deviceRevision) throw new Error("The draft changed. Review it again.");
    return current.conflict ? { ...current, current: current.conflict, deviceRevision: current.deviceRevision + 1, pending: [], conflict: null, needsReview: false, error: null, deleted: false } : null;
  });
  return copyId;
}
/** Device-only drafts are shown for review, never silently associated with a login. */
export async function deviceOutingsForImport(context: LibraryContext): Promise<DeviceOuting[]> {
  await assertContext(context);
  if (context.owner === "device") return [];
  const rows = await readForOwner("device"); await assertContext(context);
  return rows.filter(row => !row.deleted);
}
export async function importDeviceOuting(context: LibraryContext, sourceId: string): Promise<string> {
  await assertContext(context);
  if (context.owner === "device") throw new Error("Sign in before importing a device plan.");
  const db = await openCompanionDb();
  let failure: unknown;
  try {
    const id = await transaction<string>(db, [STORE, "outing-imports"], (tx, done) => {
      const key = `${context.owner}:${sourceId}`;
      const imports = tx.objectStore("outing-imports");
      const receipt = imports.get(key);
      receipt.onsuccess = () => {
        if (receipt.result) { done((receipt.result as { id: string }).id); return; }
        const store = tx.objectStore(STORE);
        const source = store.get(`device:${sourceId}`);
        source.onsuccess = () => {
          const row = source.result as DeviceOuting | undefined;
          if (!row || row.deleted) { tx.abort(); return; }
          const owned = store.index("owner").getAll(context.owner);
          owned.onsuccess = () => {
            try {
              const rows = owned.result as DeviceOuting[];
              const id = crypto.randomUUID(), now = new Date().toISOString();
              const state = row.current.content.startAt ? "saved" : "idea";
              const current: Outing = { ...row.current, id, user_id: context.owner, base_id: null, route_state: null, state, revision: 1, created_at: now, updated_at: now };
              const pending: OutingMutation[] = [{ mutationId: crypto.randomUUID(), id, owner: context.owner, operation: "save", expectedRevision: 0, baseId: null, state, content: current.content }];
              let imported: DeviceOuting = { key: `${context.owner}:${id}`, owner: context.owner, current, deviceRevision: 1, pending, conflict: null, needsReview: false, deleted: false, error: null };
              const route = row.routes?.find(route => route.id === row.current.route_state?.snapshotId);
              if (route && row.current.route_state) imported = withRoute(imported, route.route, row.current.route_state);
              if (["completed", "cancelled"].includes(row.current.state)) {
                const history: OutingMutation = { mutationId: crypto.randomUUID(), id, owner: context.owner, operation: "save", expectedRevision: imported.current.revision, baseId: null, state: row.current.state, content: current.content };
                imported = { ...imported, current: { ...imported.current, revision: imported.current.revision + 1, state: row.current.state }, deviceRevision: imported.deviceRevision + 1, pending: [...imported.pending, history] };
              }
              if (rows.length >= 200 || rows.reduce((sum, item) => sum + item.pending.length, imported.pending.length) > 100 || new TextEncoder().encode(JSON.stringify([...rows, imported])).byteLength > MAX_BYTES) { tx.abort(); return; }
              store.put(imported); imports.put({ key, owner: context.owner, sourceId, id }); done(id);
            } catch (error) { failure = error; tx.abort(); }
          };
        };
      };
    }).catch(error => { throw failure ?? error; });
    await assertContext(context); changed(); return id;
  } finally { db.close(); }
}

function withRoute(previous: DeviceOuting, raw: SavedRoute, progress?: OutingRouteState): DeviceOuting {
  const route = parseOutingRoute(raw);
  if (!route || !routeMatchesOuting(route, previous.current.content)) throw new Error("This route uses a different destination, activity or time. Review the outing before attaching it.");
  if (previous.current.state !== "saved" || previous.current.route_state && previous.current.route_state.status !== "saved") throw new Error("Keep the started route. Create another outing for a different route.");
  if ((previous.routes?.length ?? 0) >= 20) throw new Error("This outing has 20 route versions. Keep this route or make a new outing.");
  const snapshotId = crypto.randomUUID();
  const initial = routeStateFromSaved(snapshotId, route);
  const mutation: OutingRouteMutation = { operation: "route", id: previous.current.id, owner: previous.owner, mutationId: crypto.randomUUID(), expectedRevision: previous.current.revision, routeState: initial, snapshot: route };
  if (!validateRouteMutation(mutation) || initial.progress > route.plan.itinerary.legs.length || initial.status === "completed" && initial.progress !== route.plan.itinerary.legs.length) throw new Error("Review this route's saved progress before importing it.");
  let result: DeviceOuting = { ...previous, routes: [...previous.routes ?? [], { id: snapshotId, route }],
    deviceRevision: previous.deviceRevision + 1, current: { ...previous.current, revision: previous.current.revision + 1, route_state: initial, updated_at: new Date().toISOString() },
    pending: previous.owner === "device" ? [] : [...previous.pending, mutation] };
  if (progress && (progress.progress !== initial.progress || progress.status !== initial.status)) result = advanceRoute(result, { ...progress, snapshotId });
  return result;
}
function advanceRoute(previous: DeviceOuting, state: OutingRouteState): DeviceOuting {
  const before = previous.current.route_state, snapshot = previous.routes?.find(route => route.id === before?.snapshotId);
  if (!before || !snapshot || !["saved", "in_progress"].includes(previous.current.state) || !canAdvanceRoute(before, state, snapshot.route.plan.itinerary.legs.length)) throw new Error("The saved route changed. Review its progress before continuing.");
  const mutation: OutingRouteMutation = { operation: "route", id: previous.current.id, owner: previous.owner, mutationId: crypto.randomUUID(), expectedRevision: previous.current.revision, routeState: state, snapshot: null };
  if (!validateRouteMutation(mutation)) throw new Error("The route progress could not be saved.");
  return { ...previous, deviceRevision: previous.deviceRevision + 1,
    current: { ...previous.current, revision: previous.current.revision + 1, route_state: state, updated_at: new Date().toISOString() },
    pending: previous.owner === "device" ? [] : [...previous.pending, mutation] };
}
export async function saveDeviceOutingRoute(context: LibraryContext, id: string, deviceRevision: number, value: SavedRoute | OutingRouteState): Promise<DeviceOuting | null> {
  return mutateRecord(context, id, previous => {
    if (!previous || previous.deleted || previous.needsReview || previous.deviceRevision !== deviceRevision) throw new Error("Review the changed outing first.");
    return "plan" in value ? withRoute(previous, value) : advanceRoute(previous, value);
  });
}
export async function cacheOutingRoute(context: LibraryContext, id: string, snapshot: OutingRouteSnapshot): Promise<void> {
  const route = parseOutingRoute(snapshot.route);
  if (!route) throw new Error("The saved route could not be read. Your existing copy is kept.");
  await mutateRecord(context, id, previous => {
    if (!previous || previous.current.route_state?.snapshotId !== snapshot.id || previous.routes?.some(item => item.id === snapshot.id)) return previous ?? null;
    return { ...previous, routes: [...previous.routes ?? [], { id: snapshot.id, route }] };
  });
}
export async function deleteAccountOutingCache(context: LibraryContext): Promise<void> {
  await assertContext(context);
  const db = await openCompanionDb();
  try { await transaction<void>(db, [STORE, "outing-imports", DELETIONS], (tx, done) => {
    for (const name of [STORE, "outing-imports", DELETIONS]) {
      const store = tx.objectStore(name), cursor = store.openCursor();
      cursor.onsuccess = () => { const row = cursor.result; if (row) { if ((row.value as { owner: string }).owner === context.owner) row.delete(); row.continue(); } };
    }
    done();
  }); } finally { db.close(); }
}
